import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, AlertTriangle, CheckCircle, XCircle, ChevronDown, ChevronUp, BarChart3, TrendingUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { exportToCSV, downloadFile, DEFAULT_FOS_TARGETS, getFOSStatus, getFOSColor } from '@/lib/calculations';

export const ResultsTab = ({ 
  results, 
  chartRef, 
  designLoads, 
  setDesignLoads, 
  fos, 
  pileName,
  // New props for multi-pile view
  piles,
  activePileId,
  onSelectPile,
  fosTargets = DEFAULT_FOS_TARGETS
}) => {
  const [expandedSection, setExpandedSection] = useState('details');
  
  // Determine if we're in multi-pile mode
  const isMultiPileMode = piles && piles.length > 0;
  
  // Get the active pile data when in multi-pile mode
  const activePile = isMultiPileMode 
    ? piles.find(p => p.id === activePileId) 
    : null;
  
  // Use props or active pile data
  const currentResults = isMultiPileMode ? activePile?.results : results;
  const currentDesignLoads = isMultiPileMode ? activePile?.designLoads : designLoads;
  const currentFos = isMultiPileMode ? activePile?.fos : fos;
  const currentPileName = isMultiPileMode ? activePile?.name : pileName;

  // Calculate summary statistics for multi-pile mode
  const summaryStats = useMemo(() => {
    if (!isMultiPileMode) return null;
    
    const pilesWithResults = piles.filter(p => p.results);
    const passing = piles.filter(p => getFOSStatus(p.fos?.minFS) === 'PASS').length;
    const warning = piles.filter(p => getFOSStatus(p.fos?.minFS) === 'WARNING').length;
    const failing = piles.filter(p => getFOSStatus(p.fos?.minFS) === 'FAIL').length;
    
    return {
      total: piles.length,
      analyzed: pilesWithResults.length,
      passing,
      warning,
      failing,
      minCapacity: pilesWithResults.length > 0 
        ? Math.min(...pilesWithResults.map(p => p.results.ultimateCapacity)) 
        : null,
      maxCapacity: pilesWithResults.length > 0 
        ? Math.max(...pilesWithResults.map(p => p.results.ultimateCapacity)) 
        : null,
      avgCapacity: pilesWithResults.length > 0
        ? pilesWithResults.reduce((sum, p) => sum + p.results.ultimateCapacity, 0) / pilesWithResults.length
        : null
    };
  }, [piles, isMultiPileMode]);

  // Show empty state if no results anywhere
  if (!currentResults && (!isMultiPileMode || !piles.some(p => p.results))) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="results-empty-state">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">No Results Available</h3>
          <p className="text-muted-foreground mt-1">Click "Run Analysis" or "Run All" to calculate pile capacity</p>
        </div>
      </div>
    );
  }

  // Handle design load update
  const updateDesignLoad = (key, value) => {
    if (setDesignLoads) {
      setDesignLoads(prev => ({
        ...prev,
        [key]: key.includes('CaseId') ? value : parseFloat(value) || 0
      }));
    }
  };

  // Handle CSV export
  const handleExportCSV = () => {
    if (currentResults) {
      const csv = exportToCSV(currentResults);
      downloadFile(csv, 'pile_capacity_results.csv', 'text/csv');
    }
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-slate-900 border border-border rounded-lg shadow-lg p-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Settlement:</span>
            <span className="font-mono font-medium">{data.settlement} mm</span>
            <span className="text-muted-foreground">Load:</span>
            <span className="font-mono font-medium">{data.load} MN</span>
            <span className="text-muted-foreground">% of Ultimate:</span>
            <span className="font-mono font-medium">{data.percentOfUltimate}%</span>
            <span className="text-muted-foreground">Governing:</span>
            <span className="text-xs">{data.governingComponent}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Get FOS status badge
  const getFOSBadge = (status) => {
    if (!status) return null;
    if (status === 'PASS') {
      return <Badge className="bg-emerald-500 hover:bg-emerald-600"><CheckCircle className="h-3 w-3 mr-1" />PASS</Badge>;
    }
    return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />FAIL</Badge>;
  };

  // Get status badge for pile row
  const getStatusBadge = (minFS) => {
    const status = getFOSStatus(minFS);
    if (!status) return <Badge variant="outline">N/A</Badge>;
    
    const variants = {
      PASS: { className: 'bg-emerald-500 hover:bg-emerald-600', icon: CheckCircle },
      WARNING: { className: 'bg-amber-500 hover:bg-amber-600', icon: AlertTriangle },
      FAIL: { className: 'bg-red-500 hover:bg-red-600', icon: XCircle }
    };
    
    const config = variants[status];
    const Icon = config.icon;
    
    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  // Build FOS table data for detailed view
  const fosTableData = [];
  if (currentFos && currentDesignLoads) {
    if (currentDesignLoads.compOperating > 0) {
      fosTableData.push({
        case: 'Operating Compression',
        caseId: currentDesignLoads.compOperatingCaseId,
        load: currentDesignLoads.compOperating,
        fs: currentFos.compOperating,
        requirement: `≥ ${fosTargets.compOperating.toFixed(1)}`,
        status: currentFos.compOperatingStatus
      });
    }
    if (currentDesignLoads.compStorm > 0) {
      fosTableData.push({
        case: 'Storm Compression',
        caseId: currentDesignLoads.compStormCaseId,
        load: currentDesignLoads.compStorm,
        fs: currentFos.compStorm,
        requirement: `≥ ${fosTargets.compStorm.toFixed(1)}`,
        status: currentFos.compStormStatus
      });
    }
    if (currentDesignLoads.tensOperating > 0) {
      fosTableData.push({
        case: 'Operating Tension',
        caseId: currentDesignLoads.tensOperatingCaseId,
        load: currentDesignLoads.tensOperating,
        fs: currentFos.tensOperating,
        requirement: `≥ ${fosTargets.tensOperating.toFixed(1)}`,
        status: currentFos.tensOperatingStatus
      });
    }
    if (currentDesignLoads.tensStorm > 0) {
      fosTableData.push({
        case: 'Storm Tension',
        caseId: currentDesignLoads.tensStormCaseId,
        load: currentDesignLoads.tensStorm,
        fs: currentFos.tensStorm,
        requirement: `≥ ${fosTargets.tensStorm.toFixed(1)}`,
        status: currentFos.tensStormStatus
      });
    }
  }

  return (
    <div className="space-y-6" data-testid="results-tab">
      {/* Multi-Pile Summary Section */}
      {isMultiPileMode && summaryStats && (
        <>
          {/* Summary Statistics Cards - Green Theme */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Total Piles</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{summaryStats.total}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-emerald-500/50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{summaryStats.analyzed} analyzed</p>
              </CardContent>
            </Card>
            
            <Card className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">Passing</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">{summaryStats.passing}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500/50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">FS ≥ 1.5</p>
              </CardContent>
            </Card>
            
            <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Warning</p>
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{summaryStats.warning}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-500/50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">1.2 ≤ FS &lt; 1.5</p>
              </CardContent>
            </Card>
            
            <Card className="bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium">Failing</p>
                    <p className="text-2xl font-bold text-red-700 dark:text-red-300">{summaryStats.failing}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500/50" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">FS &lt; 1.2</p>
              </CardContent>
            </Card>
          </div>

          {/* Capacity Range Card */}
          {summaryStats.analyzed > 0 && (
            <Card className="border-emerald-200 dark:border-emerald-800/50">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6">
                    <TrendingUp className="h-5 w-5 text-emerald-600" />
                    <div className="flex items-center gap-8 text-sm">
                      <div>
                        <span className="text-muted-foreground">Min Q<sub>u</sub>: </span>
                        <span className="font-mono font-semibold">{summaryStats.minCapacity?.toFixed(3)} MN</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Max Q<sub>u</sub>: </span>
                        <span className="font-mono font-semibold">{summaryStats.maxCapacity?.toFixed(3)} MN</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Avg Q<sub>u</sub>: </span>
                        <span className="font-mono font-semibold">{summaryStats.avgCapacity?.toFixed(3)} MN</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Multi-Pile Results Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-emerald-600" />
                All Pile Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-emerald-50/50 dark:bg-emerald-950/20">
                    <TableHead>Pile ID</TableHead>
                    <TableHead className="text-right">Q<sub>u</sub> (MN)</TableHead>
                    <TableHead className="text-right">Q<sub>allow</sub> (MN)</TableHead>
                    <TableHead className="text-right">Min FS</TableHead>
                    <TableHead>Governing Mechanism</TableHead>
                    <TableHead>Governing Case</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {piles.map(pile => (
                    <TableRow 
                      key={pile.id}
                      className={`cursor-pointer transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-950/30 ${
                        pile.id === activePileId ? 'bg-emerald-100 dark:bg-emerald-900/40 border-l-4 border-l-emerald-500' : ''
                      }`}
                      onClick={() => onSelectPile && onSelectPile(pile.id)}
                      data-testid={`results-pile-row-${pile.id}`}
                    >
                      <TableCell className="font-medium">
                        {pile.name}
                        {pile.id === activePileId && (
                          <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">(selected)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {pile.results ? pile.results.ultimateCapacity.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {pile.results ? pile.results.allowableCapacity.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell className={`text-right font-mono font-semibold ${
                        pile.fos?.minFS >= 1.5 ? 'text-emerald-600 dark:text-emerald-400' :
                        pile.fos?.minFS >= 1.2 ? 'text-amber-600 dark:text-amber-400' :
                        pile.fos?.minFS ? 'text-red-600 dark:text-red-400' : ''
                      }`}>
                        {pile.fos?.minFS ? pile.fos.minFS.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {pile.results?.governingComponent || '—'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {pile.fos?.governingCase || '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(pile.fos?.minFS)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {/* Selected Pile Details Section */}
      {currentResults && (
        <>
          {/* Section Header for Multi-Pile Mode */}
          {isMultiPileMode && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-3">
                <div className="h-8 w-1 bg-emerald-500 rounded-full" />
                <div>
                  <h3 className="text-lg font-semibold">Detailed Results</h3>
                  <p className="text-sm text-muted-foreground">
                    Pile: <span className="font-semibold text-foreground">{currentPileName}</span>
                  </p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setExpandedSection(expandedSection === 'details' ? '' : 'details')}
              >
                {expandedSection === 'details' ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          {/* Single Pile Header (non-multi mode) */}
          {!isMultiPileMode && currentPileName && (
            <div className="text-sm text-muted-foreground">
              Results for: <span className="font-semibold text-foreground">{currentPileName}</span>
            </div>
          )}

          {/* Detailed Results Content */}
          {(expandedSection === 'details' || !isMultiPileMode) && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Ultimate Capacity */}
                <div className="metric-card bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/50 dark:to-green-950/50 border-emerald-200 dark:border-emerald-800" data-testid="ultimate-capacity-card">
                  <div className="metric-card-label text-emerald-700 dark:text-emerald-400">Ultimate Capacity</div>
                  <div className="metric-card-value text-emerald-900 dark:text-emerald-100">
                    {currentResults.ultimateCapacity.toFixed(2)}
                    <span className="metric-card-unit">MN</span>
                  </div>
                  <div className="text-xs mt-2 text-emerald-600 dark:text-emerald-500">
                    Q<sub>u</sub> = min(Q<sub>shaft</sub>, Q<sub>sg</sub>)
                  </div>
                </div>

                {/* Allowable Capacity */}
                <div className="metric-card metric-card-secondary" data-testid="allowable-capacity-card">
                  <div className="metric-card-label">Allowable Capacity (FS = {currentResults.factorOfSafety})</div>
                  <div className="metric-card-value">
                    {currentResults.allowableCapacity.toFixed(2)}
                    <span className="metric-card-unit">MN</span>
                  </div>
                  <div className="text-xs mt-2 text-muted-foreground">
                    Q<sub>allow</sub> = Q<sub>u</sub> / FS
                  </div>
                </div>

                {/* Shaft Resistance */}
                <div className="metric-card metric-card-secondary" data-testid="shaft-resistance-card">
                  <div className="metric-card-label">Shaft Resistance</div>
                  <div className="metric-card-value">
                    {currentResults.shaftCapacity.toFixed(2)}
                    <span className="metric-card-unit">MN</span>
                  </div>
                  <div className="text-xs mt-2 text-muted-foreground flex items-center gap-1">
                    {currentResults.shaftPercentage.toFixed(1)}% of total
                    {currentResults.governingComponent === 'Shaft Resistance' && (
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Design Loads Panel */}
              <Card className="border-dashed">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    Design Loads
                    <span className="text-xs font-normal text-muted-foreground">(API-style FOS calculation)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Operating Compression */}
                    <div className="space-y-2">
                      <Label className="text-xs">Operating Compression Q<sub>c,op</sub> (MN)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={currentDesignLoads?.compOperating || ''}
                          onChange={(e) => updateDesignLoad('compOperating', e.target.value)}
                          placeholder="0.00"
                          className="engineering-input"
                          data-testid="input-comp-operating"
                        />
                        <Input
                          value={currentDesignLoads?.compOperatingCaseId || ''}
                          onChange={(e) => updateDesignLoad('compOperatingCaseId', e.target.value)}
                          placeholder="Case ID"
                          className="w-20 text-xs"
                          data-testid="input-comp-operating-id"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">Target FS ≥ 2.0</div>
                    </div>

                    {/* Storm Compression */}
                    <div className="space-y-2">
                      <Label className="text-xs">Storm Compression Q<sub>c,st</sub> (MN)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={currentDesignLoads?.compStorm || ''}
                          onChange={(e) => updateDesignLoad('compStorm', e.target.value)}
                          placeholder="0.00"
                          className="engineering-input"
                          data-testid="input-comp-storm"
                        />
                        <Input
                          value={currentDesignLoads?.compStormCaseId || ''}
                          onChange={(e) => updateDesignLoad('compStormCaseId', e.target.value)}
                          placeholder="Case ID"
                          className="w-20 text-xs"
                          data-testid="input-comp-storm-id"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">Target FS ≥ 1.5</div>
                    </div>

                    {/* Operating Tension */}
                    <div className="space-y-2">
                      <Label className="text-xs">Operating Tension Q<sub>t,op</sub> (MN)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={currentDesignLoads?.tensOperating || ''}
                          onChange={(e) => updateDesignLoad('tensOperating', e.target.value)}
                          placeholder="0.00"
                          className="engineering-input"
                          data-testid="input-tens-operating"
                        />
                        <Input
                          value={currentDesignLoads?.tensOperatingCaseId || ''}
                          onChange={(e) => updateDesignLoad('tensOperatingCaseId', e.target.value)}
                          placeholder="Case ID"
                          className="w-20 text-xs"
                          data-testid="input-tens-operating-id"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">Target FS ≥ 1.5</div>
                    </div>

                    {/* Storm Tension */}
                    <div className="space-y-2">
                      <Label className="text-xs">Storm Tension Q<sub>t,st</sub> (MN)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          value={currentDesignLoads?.tensStorm || ''}
                          onChange={(e) => updateDesignLoad('tensStorm', e.target.value)}
                          placeholder="0.00"
                          className="engineering-input"
                          data-testid="input-tens-storm"
                        />
                        <Input
                          value={currentDesignLoads?.tensStormCaseId || ''}
                          onChange={(e) => updateDesignLoad('tensStormCaseId', e.target.value)}
                          placeholder="Case ID"
                          className="w-20 text-xs"
                          data-testid="input-tens-storm-id"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">Target FS ≥ 1.5</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FOS Results Table */}
              {fosTableData.length > 0 && (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Factor of Safety Check</CardTitle>
                    {currentFos?.minFS && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Governing: </span>
                        <span className="font-semibold">{currentFos.governingCase}</span>
                        <span className="text-muted-foreground ml-2">Min FS = </span>
                        <span className={`font-mono font-bold ${currentFos.minFS >= 1.5 ? 'text-emerald-600' : currentFos.minFS >= 1.2 ? 'text-amber-600' : 'text-red-600'}`}>
                          {currentFos.minFS.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Load Case</TableHead>
                          <TableHead className="text-right">Applied Load (MN)</TableHead>
                          <TableHead className="text-right">Ultimate Q<sub>u</sub> (MN)</TableHead>
                          <TableHead className="text-right">FS</TableHead>
                          <TableHead className="text-center">Requirement</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {fosTableData.map((row, index) => (
                          <TableRow key={index} data-testid={`fos-row-${index}`}>
                            <TableCell className="font-medium">
                              {row.case}
                              {row.caseId && <span className="text-xs text-muted-foreground ml-2">({row.caseId})</span>}
                            </TableCell>
                            <TableCell className="text-right font-mono">{row.load.toFixed(3)}</TableCell>
                            <TableCell className="text-right font-mono">{currentResults.ultimateCapacity.toFixed(3)}</TableCell>
                            <TableCell className={`text-right font-mono font-bold ${row.status === 'PASS' ? 'text-emerald-600' : 'text-red-600'}`}>
                              {row.fs?.toFixed(2) || '—'}
                            </TableCell>
                            <TableCell className="text-center text-sm">{row.requirement}</TableCell>
                            <TableCell className="text-center">{getFOSBadge(row.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Governing Component Banner */}
              <Card className="border-emerald-300/50 bg-emerald-50/50 dark:bg-emerald-950/20">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      <div>
                        <span className="text-sm text-muted-foreground">Governing Mechanism: </span>
                        <span className="font-semibold" data-testid="governing-mechanism">
                          {currentResults.governingComponent}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleExportCSV} data-testid="export-csv-btn">
                      <Download className="h-4 w-4 mr-1" />
                      Download CSV
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Capacity Breakdown Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Capacity Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Component</TableHead>
                        <TableHead className="text-right">Depth (m)</TableHead>
                        <TableHead className="text-right">q<sub>u</sub> (MPa)</TableHead>
                        <TableHead className="text-right">T<sub>RG</sub> (MPa)</TableHead>
                        <TableHead className="text-right">Contribution (MN)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Steel-Grout Row */}
                      <TableRow data-testid="breakdown-steel-grout">
                        <TableCell className="font-medium">Steel-Grout Connection</TableCell>
                        <TableCell className="text-right font-mono">—</TableCell>
                        <TableCell className="text-right font-mono">—</TableCell>
                        <TableCell className="text-right font-mono">—</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {currentResults.steelGroutConnectionCapacity.toFixed(3)}
                        </TableCell>
                      </TableRow>

                      {/* Layer Rows */}
                      {currentResults.layerContributions.map((layer, index) => (
                        <TableRow key={index} data-testid={`breakdown-layer-${index}`}>
                          <TableCell className="font-medium">Layer {layer.layerIndex}</TableCell>
                          <TableCell className="text-right font-mono">
                            {layer.from.toFixed(1)} – {layer.to.toFixed(1)}
                          </TableCell>
                          <TableCell className="text-right font-mono">{layer.qu.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono">{layer.trg.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-mono">{layer.contribution.toFixed(3)}</TableCell>
                        </TableRow>
                      ))}

                      {/* Total Shaft Row */}
                      <TableRow className="bg-muted/50" data-testid="breakdown-total-shaft">
                        <TableCell className="font-semibold" colSpan={4}>Total Shaft Capacity</TableCell>
                        <TableCell className="text-right font-mono font-semibold">
                          {currentResults.shaftCapacity.toFixed(3)}
                        </TableCell>
                      </TableRow>

                      {/* Ultimate Capacity Row */}
                      <TableRow className="bg-emerald-100/50 dark:bg-emerald-900/30" data-testid="breakdown-ultimate">
                        <TableCell className="font-bold text-emerald-700 dark:text-emerald-400" colSpan={4}>
                          Ultimate Capacity (Governing)
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-700 dark:text-emerald-400">
                          {currentResults.ultimateCapacity.toFixed(3)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Load-Settlement Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Load vs Settlement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div ref={chartRef} className="h-[400px]" data-testid="load-settlement-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={currentResults.loadSettlementData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" />
                        <XAxis
                          dataKey="settlement"
                          stroke="#64748b"
                          fontSize={12}
                          label={{ value: 'Settlement (mm)', position: 'bottom', offset: 0 }}
                        />
                        <YAxis
                          stroke="#64748b"
                          fontSize={12}
                          width={60}
                          label={{ value: 'Load (MN)', angle: -90, position: 'insideLeft', offset: 10 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine
                          y={currentResults.ultimateCapacity}
                          stroke="#f97316"
                          strokeDasharray="5 5"
                          label={{
                            value: `Qu = ${currentResults.ultimateCapacity.toFixed(2)} MN`,
                            position: 'right',
                            fill: '#f97316',
                            fontSize: 11
                          }}
                        />
                        <ReferenceLine
                          y={currentResults.allowableCapacity}
                          stroke="#10b981"
                          strokeDasharray="3 3"
                          label={{
                            value: `Qallow = ${currentResults.allowableCapacity.toFixed(2)} MN`,
                            position: 'right',
                            fill: '#10b981',
                            fontSize: 11
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="load"
                          stroke="#059669"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, fill: '#059669' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Steel Properties</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Steel Area</span>
                      <span className="font-mono">{currentResults.totalSteelArea.toFixed(0)} mm²</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average Diameter</span>
                      <span className="font-mono">{currentResults.avgDiameter.toFixed(0)} mm</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Steel Capacity</span>
                      <span className="font-mono">{currentResults.steelCapacityMN.toFixed(3)} MN</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Steel-Grout Bond Capacity</span>
                      <span className="font-mono">{currentResults.steelGroutCapacityMN.toFixed(3)} MN</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Pile Geometry</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Pile Length</span>
                      <span className="font-mono">{currentResults.totalPileLength.toFixed(1)} m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Embedded Length</span>
                      <span className="font-mono">{currentResults.embeddedLength.toFixed(1)} m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shaft % of Capacity</span>
                      <span className="font-mono">{currentResults.shaftPercentage.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Factor of Safety</span>
                      <span className="font-mono">{currentResults.factorOfSafety}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
